/**
 * Customer Service Management
 *
 * Handles operations on customer services (Starlink, NBN, etc.)
 * - Create/activate services
 * - Update service metadata (kit numbers, etc.)
 * - Deactivate services
 * - Fetch customer services
 */

import { collection, addDoc, updateDoc, doc, getDoc, query, where, getDocs, orderBy, deleteField, writeBatch, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { CustomerService, ServicePriceChange } from '../types/schema';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import { logCustomerActivity, resolveActorName } from '@/services/aggregate-customer.service';
import { billsRecurringInUtilmate, initialUtilmateHandoff } from './utilmate-handoff.service';

/** Firestore batches cap at 500 writes — leave headroom */
const CASCADE_BATCH_LIMIT = 450;

/**
 * Create/activate a new service for a customer
 */
export async function createCustomerService(
  customerId: string,
  customerName: string,
  serviceType: CustomerService['serviceType'],
  options: {
    billingType?: 'recurring' | 'one-time';
    addressBound?: boolean;
    jobId?: string;
    metadata?: CustomerService['metadata'];
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    notes?: string;
    installationNotes?: string;
    monthlyPrice?: number;
    planId?: string;
    planName?: string;
    dealId?: string;
    /**
     * Mark the monthlyPrice as a deliberate override of the underlying plan's
     * sellPrice (e.g. legacy/grandfathered, bundle pricing, retention discount).
     * When set, a creation-time entry is appended to priceHistory so we can
     * always answer "why is this customer paying $X?" later.
     */
    initialPriceOverride?: {
      planSellPrice: number; // What the plan would normally charge — for the audit message
      reason?: string;       // Optional reason; defaults to a generic message
    };
  } = {}
): Promise<string> {
  try {
    const userId = localStorage.getItem('user_id') || 'unknown';
    const userName = localStorage.getItem('logged_in_user') || localStorage.getItem('user_name') || 'Unknown User';
    const now = new Date().toISOString();

    // If the caller flagged this as a price override, build a creation-time
    // audit entry. Only fires when monthlyPrice differs from the plan's sellPrice
    // — same price = not really an override.
    const priceHistory: CustomerService['priceHistory'] = (() => {
      if (!options.initialPriceOverride || typeof options.monthlyPrice !== 'number') return undefined;
      const planPrice = options.initialPriceOverride.planSellPrice;
      if (Math.round(options.monthlyPrice * 100) === Math.round(planPrice * 100)) return undefined;
      return [{
        previousPrice: null,
        newPrice: options.monthlyPrice,
        reason: options.initialPriceOverride.reason?.trim()
          || `Custom price set at creation — plan price is $${planPrice.toFixed(2)}/mo`,
        changedAt: now,
        changedBy: userId,
        changedByName: userName,
      }];
    })();

    const service: Omit<CustomerService, 'id'> = {
      customerId,
      customerName,
      serviceType,
      tenantId: getTenantIdSync(),
      billingType: options.billingType || 'recurring',
      addressBound: options.addressBound || false,
      status: 'Active',
      installedAt: now,
      installedBy: userId,
      installedByName: userName,
      jobId: options.jobId,
      planId: options.planId,
      planName: options.planName,
      dealId: options.dealId,
      monthlyPrice: options.monthlyPrice,
      priceHistory,
      metadata: options.metadata || {},
      address: options.address,
      city: options.city,
      state: options.state,
      zipCode: options.zipCode,
      notes: options.notes,
      installationNotes: options.installationNotes,
      createdAt: now,
      createdBy: userId,
    };

    // Recurring services bill in Utilmate — enroll manual adds in the same
    // hand-off lifecycle the deal-billing path uses, so services added via
    // /home/services don't silently skip the Utilmate drift panel.
    if ((options.billingType || 'recurring') === 'recurring' && billsRecurringInUtilmate(serviceType)) {
      service.utilmateHandoff = initialUtilmateHandoff();
    }

    // Strip undefined values — Firestore rejects them
    const cleanService = Object.fromEntries(
      Object.entries(service).filter(([, v]) => v !== undefined)
    );

    const docRef = await addDoc(collection(db, 'customer_services'), cleanService);
    console.log('✅ Customer service created:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creating customer service:', error);
    throw error;
  }
}

/**
 * Update service metadata (kit number, serial numbers, etc.)
 */
export async function updateServiceMetadata(
  serviceId: string,
  metadata: CustomerService['metadata']
): Promise<void> {
  try {
    const userId = localStorage.getItem('user_id') || 'unknown';
    const now = new Date().toISOString();

    const serviceRef = doc(db, 'customer_services', serviceId);
    await updateDoc(serviceRef, {
      metadata,
      updatedAt: now,
      updatedBy: userId,
    });

    console.log('✅ Service metadata updated:', serviceId);
  } catch (error) {
    console.error('❌ Error updating service metadata:', error);
    throw error;
  }
}

/**
 * Update service status
 */
export async function updateServiceStatus(
  serviceId: string,
  status: CustomerService['status'],
  reason?: string
): Promise<void> {
  try {
    const userId = localStorage.getItem('user_id') || 'unknown';
    const now = new Date().toISOString();

    const updates: Record<string, unknown> = {
      status,
      updatedAt: now,
      updatedBy: userId,
    };

    if (status === 'Cancelled' || status === 'Inactive') {
      updates.deactivatedAt = now;
      updates.deactivatedBy = userId;
      if (reason) {
        updates.deactivationReason = reason;
      }
    }

    const serviceRef = doc(db, 'customer_services', serviceId);
    await updateDoc(serviceRef, updates);

    console.log('✅ Service status updated:', serviceId, status);
  } catch (error) {
    console.error('❌ Error updating service status:', error);
    throw error;
  }
}

/**
 * Fetch all services for a customer
 */
export async function getCustomerServices(customerId: string): Promise<CustomerService[]> {
  try {
    const servicesQuery = query(
      collection(db, 'customer_services'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(servicesQuery);
    const services = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    })) as CustomerService[];

    console.log(`✅ Loaded ${services.length} services for customer ${customerId}`);
    return services;
  } catch (error) {
    console.error('❌ Error fetching customer services:', error);
    throw error;
  }
}

/**
 * Get active services only
 */
export async function getActiveCustomerServices(customerId: string): Promise<CustomerService[]> {
  try {
    const servicesQuery = query(
      collection(db, 'customer_services'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('customerId', '==', customerId),
      where('status', '==', 'Active'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(servicesQuery);
    const services = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    })) as CustomerService[];

    console.log(`✅ Loaded ${services.length} active services for customer ${customerId}`);
    return services;
  } catch (error) {
    console.error('❌ Error fetching active customer services:', error);
    throw error;
  }
}

/**
 * Customer 360 audit log helper — fire-and-forget. Re-fetches the service
 * to recover customerId + display fields for the summary. Logging failure
 * NEVER rolls back the surrounding write. Day 5.
 */
function logServiceChangeForAudit(
  serviceRef: ReturnType<typeof doc>,
  serviceId: string,
  action: string,
  userId: string,
  now: string,
): void {
  void (async () => {
    try {
      const snap = await getDoc(serviceRef);
      if (!snap.exists()) return;
      const svc = snap.data() as CustomerService;
      if (!svc.customerId) return;
      await logCustomerActivity({
        customerId: svc.customerId,
        actorId: userId,
        actorName: resolveActorName(userId),
        entryType: 'service_change',
        summary: `Service ${svc.serviceType}${svc.planName ? ` (${svc.planName})` : ''} — ${action}`,
        sourceEntity: 'service',
        sourceId: serviceId,
        timestamp: now,
      });
    } catch (err) {
      console.error('[customer-service] customer audit log failed:', err);
    }
  })();
}

/**
 * Suspend a service (pause billing, service still exists)
 * Used for temporary service interruptions or payment issues
 */
export async function suspendService(
  serviceId: string,
  reason: string
): Promise<void> {
  try {
    const userId = localStorage.getItem('user_id') || 'unknown';
    const now = new Date().toISOString();

    const serviceRef = doc(db, 'customer_services', serviceId);
    await updateDoc(serviceRef, {
      status: 'Suspended',
      suspendedAt: now,
      deactivationReason: reason,
      updatedAt: now,
      updatedBy: userId,
    });

    logServiceChangeForAudit(serviceRef, serviceId, `suspended (${reason})`, userId, now);

    // Fire-and-forget: surface the suspension on any open faults linked to
    // this service so ops isn't investigating against a suspended service.
    cascadeServiceStatusToOpenFaults(
      serviceId, 'suspended', reason,
      localStorage.getItem('logged_in_user') || 'Office'
    ).catch((err) => console.error('[suspendService] Fault cascade failed:', err));

    console.log('✅ Service suspended:', serviceId);
  } catch (error) {
    console.error('❌ Error suspending service:', error);
    throw error;
  }
}

/**
 * Reactivate a suspended service
 */
export async function reactivateService(serviceId: string): Promise<void> {
  try {
    const userId = localStorage.getItem('user_id') || 'unknown';
    const now = new Date().toISOString();

    const serviceRef = doc(db, 'customer_services', serviceId);
    await updateDoc(serviceRef, {
      status: 'Active',
      suspendedAt: deleteField(),
      deactivationReason: deleteField(),
      updatedAt: now,
      updatedBy: userId,
    });

    logServiceChangeForAudit(serviceRef, serviceId, 'reactivated', userId, now);

    cascadeServiceStatusToOpenFaults(
      serviceId, 'reactivated', undefined,
      localStorage.getItem('logged_in_user') || 'Office'
    ).catch((err) => console.error('[reactivateService] Fault cascade failed:', err));

    console.log('✅ Service reactivated:', serviceId);
  } catch (error) {
    console.error('❌ Error reactivating service:', error);
    throw error;
  }
}

/**
 * Best-effort office follow-up todo for a service lifecycle action
 * (cancellation / move-out). Assigned to the acting user so it lands on
 * their Plate; never blocks the service write.
 */
async function createServiceLifecycleTodo(args: {
  service: Pick<CustomerService, 'customerName' | 'serviceType' | 'planName' | 'customerId'>;
  title: string;
  description: string;
  dueDate?: string; // YYYY-MM-DD
}): Promise<void> {
  const { createTodo } = await import('./todo.service');
  const userId = localStorage.getItem('user_id') || 'unknown';
  const userName = localStorage.getItem('logged_in_user') || 'Office';
  const now = new Date().toISOString();
  await createTodo({
    tenantId: getTenantIdSync(),
    title: args.title,
    completed: false,
    order: 0,
    dueDate: args.dueDate || undefined,
    createdBy: userId,
    createdByName: userName,
    assignees: [{ userId, userName, role: localStorage.getItem('user_role') || 'Office', addedAt: now }],
    entityKind: 'customer',
    sourceLabel: `${args.service.customerName} — ${args.service.serviceType}${args.service.planName ? ` (${args.service.planName})` : ''}`,
    sourcePath: '/home/services',
    description: args.description,
  });
}

/**
 * Cancel a service permanently
 * Used when customer terminates service
 *
 * @param options.effectiveDate  YYYY-MM-DD the cancellation takes effect
 *                               (service is marked Cancelled immediately;
 *                               the date is recorded + drives the office todo).
 * @param options.createOfficeTodo  default true — drop a follow-up todo on the
 *                                  acting user's Plate (billing stop, returns).
 */
export async function cancelService(
  serviceId: string,
  reason: string,
  options?: { effectiveDate?: string; createOfficeTodo?: boolean }
): Promise<void> {
  try {
    const userId = localStorage.getItem('user_id') || 'unknown';
    const now = new Date().toISOString();

    const serviceRef = doc(db, 'customer_services', serviceId);
    await updateDoc(serviceRef, {
      status: 'Cancelled',
      cancelledAt: now,
      cancellationReason: reason,
      ...(options?.effectiveDate ? { cancellationEffectiveDate: options.effectiveDate } : {}),
      deactivatedAt: now,
      deactivatedBy: userId,
      updatedAt: now,
      updatedBy: userId,
    });

    logServiceChangeForAudit(serviceRef, serviceId, `cancelled (${reason})`, userId, now);

    // Fire-and-forget: surface the cancellation on any open faults linked to
    // this service — a fault shouldn't sit open against a dead service with
    // no signal on the fault record.
    cascadeServiceStatusToOpenFaults(
      serviceId, 'cancelled', reason,
      localStorage.getItem('logged_in_user') || 'Office'
    ).catch((err) => console.error('[cancelService] Fault cascade failed:', err));

    // Fire-and-forget: office follow-up todo so the cancellation date has an
    // owner (stop billing in Utilmate, arrange equipment return, etc.).
    if (options?.createOfficeTodo !== false) {
      getDoc(serviceRef)
        .then((snap) => {
          const s = (snap.data() || {}) as CustomerService;
          return createServiceLifecycleTodo({
            service: s,
            title: `Process service cancellation — ${s.customerName || 'customer'} (${s.serviceType || 'service'})`,
            description: `Reason: ${reason}${options?.effectiveDate ? `\nEffective: ${options.effectiveDate}` : ''}\nStop recurring billing / arrange equipment return as needed.`,
            dueDate: options?.effectiveDate,
          });
        })
        .catch((err) => console.error('[cancelService] Office todo failed:', err));
    }

    console.log('✅ Service cancelled:', serviceId);
  } catch (error) {
    console.error('❌ Error cancelling service:', error);
    throw error;
  }
}

/**
 * "Moving out" — the occupant is leaving but the installed service/equipment
 * stays with the site. Cancels the service (billing must stop) while:
 *  - preserving the full service record + notes (nothing is deleted),
 *  - stamping `movedOut` / `movedOutAt` so the card reads "Moved out", and
 *  - archiving a snapshot onto the customer's `oldServiceNotes` so if a new
 *    occupant takes the service over, the site history is one lookup away.
 */
export async function markServiceMovedOut(
  serviceId: string,
  options: { moveOutDate?: string; note?: string; createOfficeTodo?: boolean } = {}
): Promise<void> {
  const userId = localStorage.getItem('user_id') || 'unknown';
  const userName = localStorage.getItem('logged_in_user') || 'Office';
  const now = new Date().toISOString();
  // Date-only fallback must be built from LOCAL components — toISOString()
  // is UTC and returns yesterday's date before ~10-11am Sydney (#1463 H1).
  const d = new Date();
  const localToday = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const serviceRef = doc(db, 'customer_services', serviceId);
  const snap = await getDoc(serviceRef);
  if (!snap.exists()) throw new Error('Service not found');
  const service = { id: serviceId, ...snap.data() } as CustomerService;

  const reason = options.note?.trim()
    ? `Customer moved out — ${options.note.trim()}`
    : 'Customer moved out';

  await updateDoc(serviceRef, {
    status: 'Cancelled',
    cancelledAt: now,
    cancellationReason: reason,
    ...(options.moveOutDate ? { cancellationEffectiveDate: options.moveOutDate } : {}),
    movedOut: true,
    movedOutAt: options.moveOutDate || localToday,
    deactivatedAt: now,
    deactivatedBy: userId,
    updatedAt: now,
    updatedBy: userId,
  });

  // Archive the service snapshot under the CUSTOMER's old-service notes so
  // their record keeps the history even after the service is dead.
  if (service.customerId) {
    const entry = {
      serviceId,
      serviceType: service.serviceType || 'Other',
      planName: service.planName || '',
      address: service.address || '',
      notes: [service.notes, service.installationNotes].filter(Boolean).join('\n\n'),
      moveOutNote: options.note?.trim() || '',
      movedOutAt: options.moveOutDate || localToday,
      recordedAt: now,
      recordedBy: userId,
      recordedByName: userName,
    };
    updateDoc(doc(db, 'customers', service.customerId), {
      oldServiceNotes: arrayUnion(entry),
      updatedAt: now,
    }).catch((err) => console.error('[markServiceMovedOut] Customer archive failed:', err));
  }

  logServiceChangeForAudit(serviceRef, serviceId, `moved out (${reason})`, userId, now);

  cascadeServiceStatusToOpenFaults(
    serviceId, 'cancelled', reason, userName
  ).catch((err) => console.error('[markServiceMovedOut] Fault cascade failed:', err));

  if (options.createOfficeTodo !== false) {
    createServiceLifecycleTodo({
      service,
      title: `Moving out — ${service.customerName || 'customer'} (${service.serviceType || 'service'})`,
      description: `${reason}\nAddress: ${service.address || '—'}\nService notes are archived on the customer + kept on the cancelled service. If a new occupant takes over, use Transfer to move the service to them.`,
      dueDate: options.moveOutDate,
    }).catch((err) => console.error('[markServiceMovedOut] Office todo failed:', err));
  }

  console.log('✅ Service marked moved out:', serviceId);
}

/**
 * Soft-delete a service (must be Cancelled or Inactive).
 * Sets deleted flag so queries can filter it out.
 */
export async function deleteService(serviceId: string): Promise<void> {
  try {
    const userId = localStorage.getItem('user_id') || 'unknown';
    const userName = localStorage.getItem('logged_in_user') || 'Unknown';
    const now = new Date().toISOString();

    const serviceRef = doc(db, 'customer_services', serviceId);
    await updateDoc(serviceRef, {
      deleted: true,
      deletedAt: now,
      deletedBy: userId,
      updatedAt: now,
      updatedBy: userId,
    });

    console.log('✅ Service deleted:', serviceId, 'by', userName);
  } catch (error) {
    console.error('❌ Error deleting service:', error);
    throw error;
  }
}

/**
 * Mark a Pending Install service as Active after installation completes
 */
export async function markServiceAsInstalled(
  serviceId: string,
  jobId: string
): Promise<void> {
  try {
    const userId = localStorage.getItem('user_id') || 'unknown';
    const userName = localStorage.getItem('user_name') || 'Unknown User';
    const now = new Date().toISOString();

    const serviceRef = doc(db, 'customer_services', serviceId);
    await updateDoc(serviceRef, {
      status: 'Active',
      installedAt: now,
      installedBy: userId,
      installedByName: userName,
      jobId,
      updatedAt: now,
      updatedBy: userId,
    });

    console.log('✅ Service marked as installed:', serviceId);
  } catch (error) {
    console.error('❌ Error marking service as installed:', error);
    throw error;
  }
}

/**
 * Update service billing information
 */
export async function updateServiceBilling(
  serviceId: string,
  updates: {
    monthlyPrice?: number;
    billingCycle?: CustomerService['billingCycle'];
    nextBillingDate?: string;
    accountNumber?: string;
  }
): Promise<void> {
  try {
    const userId = localStorage.getItem('user_id') || 'unknown';
    const now = new Date().toISOString();

    const serviceRef = doc(db, 'customer_services', serviceId);
    await updateDoc(serviceRef, {
      ...updates,
      updatedAt: now,
      updatedBy: userId,
    });

    console.log('✅ Service billing updated:', serviceId);
  } catch (error) {
    console.error('❌ Error updating service billing:', error);
    throw error;
  }
}

/**
 * Create a bundle of customer services in a single atomic write.
 *
 * Generates a shared bundleId, marks the first service as the bundle primary
 * (carries the full monthlyPrice), and gives every other member monthlyPrice: 0
 * so MRR summation just works. All services share the same planName (the
 * bundle name) and the same priceHistory entry on the primary.
 *
 * Used when the office wants to bill one combined "Triple Play Bundle" at
 * $200/mo even though it contains a Starlink + NBN + VoIP under the hood.
 * Per-service metadata (kit numbers, service IDs, etc.) is left empty; the
 * office fills it in afterwards by editing each individual service.
 */
export async function createBundledServices(
  customerId: string,
  customerName: string,
  bundle: {
    name: string;          // Bundle display name — written to planName on every member
    monthlyPrice: number;  // Total bundle price — written to the primary; others = 0
    reason?: string;       // Optional audit reason for the priceHistory entry on the primary
    services: {
      serviceType: CustomerService['serviceType'];
      planId?: string;
      planSellPrice?: number; // Plan default — used to detect "is this an override" for audit
    }[];
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    notes?: string;
  }
): Promise<{ bundleId: string; serviceIds: string[] }> {
  if (!bundle.services || bundle.services.length < 2) {
    throw new Error('A bundle must contain at least 2 services');
  }
  if (!bundle.name.trim()) {
    throw new Error('Bundle name is required');
  }
  if (!Number.isFinite(bundle.monthlyPrice) || bundle.monthlyPrice < 0) {
    throw new Error('Bundle monthly price must be a non-negative number');
  }

  const userId = localStorage.getItem('user_id') || 'unknown';
  const userName = localStorage.getItem('logged_in_user') || localStorage.getItem('user_name') || 'Unknown User';
  const now = new Date().toISOString();
  const tenantId = getTenantIdSync();

  // Generate a stable bundleId. crypto.randomUUID is well supported in modern
  // browsers; fall back to a timestamp-random combo just in case.
  const bundleId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  const bundleName = bundle.name.trim();
  const totalPlanPrice = bundle.services.reduce((sum, s) => sum + (s.planSellPrice || 0), 0);

  const batch = writeBatch(db);
  const serviceIds: string[] = [];

  bundle.services.forEach((member, idx) => {
    const isPrimary = idx === 0;
    const ref = doc(collection(db, 'customer_services'));
    serviceIds.push(ref.id);

    // Only the primary records the bundle price + audit entry. Others store 0
    // so MRR sums work without special handling.
    const memberMonthlyPrice = isPrimary ? bundle.monthlyPrice : 0;
    const priceHistory: ServicePriceChange[] | undefined = isPrimary
      ? [{
          previousPrice: null,
          newPrice: bundle.monthlyPrice,
          reason: bundle.reason?.trim()
            || `Bundle pricing — "${bundleName}"${totalPlanPrice > 0 ? ` (sum of plans: $${totalPlanPrice.toFixed(2)}/mo)` : ''}`,
          changedAt: now,
          changedBy: userId,
          changedByName: userName,
        }]
      : undefined;

    const docData: Omit<CustomerService, 'id'> = {
      customerId,
      customerName,
      serviceType: member.serviceType,
      tenantId,
      billingType: 'recurring',
      addressBound: false,
      status: 'Active',
      installedAt: now,
      installedBy: userId,
      installedByName: userName,
      planId: member.planId,
      planName: bundleName,
      monthlyPrice: memberMonthlyPrice,
      priceHistory,
      bundleId,
      bundlePrimary: isPrimary,
      address: bundle.address,
      city: bundle.city,
      state: bundle.state,
      zipCode: bundle.zipCode,
      notes: bundle.notes,
      installationNotes: `Created as part of bundle "${bundleName}"`,
      metadata: {},
      createdAt: now,
      createdBy: userId,
    };

    // Strip undefined — Firestore rejects them
    const clean = Object.fromEntries(
      Object.entries(docData).filter(([, v]) => v !== undefined)
    );

    batch.set(ref, clean);
  });

  await batch.commit();
  console.log(`✅ Bundle created: "${bundleName}" (${bundleId}) — ${serviceIds.length} services, $${bundle.monthlyPrice.toFixed(2)}/mo`);
  return { bundleId, serviceIds };
}

/**
 * Update a service's monthly price with an audit trail entry.
 *
 * Used for grandfathered/legacy pricing where a customer pays a different
 * rate than the current plan's sellPrice. Every change appends to
 * `priceHistory` so we can always answer "who changed this customer's
 * price, when, and why".
 *
 * @param serviceId   customer_services document ID
 * @param newPrice    New monthlyPrice (must be >= 0)
 * @param reason      Why the price was changed — required for audit
 * @param previousPrice  The price before this change. Pass `null` when setting
 *                       a price for the first time. Caller is responsible for
 *                       reading the current value (typically from the live
 *                       service object the user is editing).
 */
export async function updateServicePrice(
  serviceId: string,
  newPrice: number,
  reason: string,
  previousPrice: number | null,
): Promise<void> {
  if (!Number.isFinite(newPrice) || newPrice < 0) {
    throw new Error('Price must be a non-negative number');
  }
  const trimmedReason = reason.trim();
  if (!trimmedReason) {
    throw new Error('A reason is required when changing a service price');
  }

  const userId = localStorage.getItem('user_id') || 'unknown';
  const userName = localStorage.getItem('logged_in_user') || 'Unknown User';
  const now = new Date().toISOString();

  const change: ServicePriceChange = {
    previousPrice,
    newPrice,
    reason: trimmedReason,
    changedAt: now,
    changedBy: userId,
    changedByName: userName,
  };

  const serviceRef = doc(db, 'customer_services', serviceId);
  await updateDoc(serviceRef, {
    monthlyPrice: newPrice,
    priceHistory: arrayUnion(change),
    updatedAt: now,
    updatedBy: userId,
  });

  console.log('✅ Service price updated:', serviceId, `$${previousPrice ?? '—'} → $${newPrice}`);
}

/**
 * Calculate total Monthly Recurring Revenue (MRR) for a customer
 */
export async function calculateCustomerMRR(customerId: string): Promise<number> {
  try {
    const services = await getActiveCustomerServices(customerId);
    const mrr = services.reduce((total, service) => {
      return total + (service.monthlyPrice || 0);
    }, 0);

    console.log(`✅ Customer MRR calculated: $${mrr.toFixed(2)} for ${customerId}`);
    return mrr;
  } catch (error) {
    console.error('❌ Error calculating customer MRR:', error);
    throw error;
  }
}

/**
 * Get service by ID
 */
export async function getServiceById(serviceId: string): Promise<CustomerService | null> {
  try {
    const snap = await getDoc(doc(db, 'customer_services', serviceId));
    if (!snap.exists()) {
      return null;
    }

    return {
      id: snap.id,
      ...snap.data()
    } as CustomerService;
  } catch (error) {
    console.error('❌ Error fetching service by ID:', error);
    throw error;
  }
}

/**
 * Get address-bound services at a specific address.
 * Returns services that are tied to the site (WiFi Extension, Security, etc.)
 * regardless of which customer currently owns them.
 */
export async function getServicesByAddress(address: string): Promise<CustomerService[]> {
  try {
    const servicesQuery = query(
      collection(db, 'customer_services'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('addressBound', '==', true),
      where('address', '==', address),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(servicesQuery);
    const services = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data()
    })) as CustomerService[];

    return services;
  } catch (error) {
    console.error('❌ Error fetching services by address:', error);
    throw error;
  }
}

/**
 * Transfer a service to a different customer.
 *
 * Updates customerId + customerName on the service and cascades the
 * change to every fault linked via `serviceId`. Without this cascade,
 * a transferred service's fault history stays attached to the old
 * customer's clientName/customerId — which is one of the fragmentation
 * sources flagged in the audit (service transfer doesn't move its
 * fault history).
 *
 * Jobs and deals are NOT cascaded: they're usually attached via
 * dealId/faultId, not serviceId, so their customer linkage is
 * independent and shouldn't move implicitly.
 */
export async function transferServiceToCustomer(
  serviceId: string,
  newCustomerId: string,
  newCustomerName: string,
  reason?: string
): Promise<void> {
  try {
    const userId = localStorage.getItem('user_id') || 'unknown';
    const now = new Date().toISOString();

    const serviceRef = doc(db, 'customer_services', serviceId);
    await updateDoc(serviceRef, {
      customerId: newCustomerId,
      customerName: newCustomerName,
      updatedAt: now,
      updatedBy: userId,
      lastTransfer: {
        type: 'customer',
        transferredAt: now,
        transferredBy: userId,
        reason: reason || '',
      },
    });

    // Fire-and-forget cascade to linked faults. The transfer succeeds even
    // if the cascade fails — the service is the source of truth and the
    // backfill tool can heal stragglers.
    cascadeServiceTransferToFaults(serviceId, newCustomerId, newCustomerName).catch((err) => {
      console.error('[transferServiceToCustomer] Fault cascade failed:', err);
    });

    console.log('✅ Service transferred to customer:', newCustomerName);
  } catch (error) {
    console.error('❌ Error transferring service to customer:', error);
    throw error;
  }
}

/**
 * Cascade a service's customer change to every fault with `serviceId == this`.
 * Updates customerId + clientName on each matched fault. Batched at 450 writes.
 */
async function cascadeServiceTransferToFaults(
  serviceId: string,
  newCustomerId: string,
  newCustomerName: string
): Promise<void> {
  const snap = await getDocs(
    query(
      collection(db, 'faults'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('serviceId', '==', serviceId),
    )
  );
  if (snap.empty) return;

  const now = new Date().toISOString();
  let batch = writeBatch(db);
  let count = 0;
  let touched = 0;

  for (const d of snap.docs) {
    const data = d.data();
    // Skip if both fields are already correct (idempotent)
    if (data.customerId === newCustomerId && data.clientName === newCustomerName) continue;
    batch.update(doc(db, 'faults', d.id), {
      customerId: newCustomerId,
      clientName: newCustomerName,
      updatedAt: now,
    });
    count++;
    touched++;
    if (count >= CASCADE_BATCH_LIMIT) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count > 0) await batch.commit();

  if (touched > 0) {
    console.log(
      `[cascadeServiceTransferToFaults] Re-pointed ${touched} fault(s) on service ${serviceId} → customer ${newCustomerName}`
    );
  }
}

/**
 * Notify open faults linked to a service when its status changes
 * (suspended / cancelled / reactivated). Previously a fault could sit open
 * against a cancelled service with no signal on the fault record — ops only
 * found out by opening the service page. Appends an investigation-history
 * entry on each open (non-archived) fault via arrayUnion; fire-and-forget.
 */
async function cascadeServiceStatusToOpenFaults(
  serviceId: string,
  statusLabel: 'suspended' | 'cancelled' | 'reactivated',
  reason: string | undefined,
  byName: string
): Promise<void> {
  const snap = await getDocs(
    query(
      collection(db, 'faults'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('serviceId', '==', serviceId),
    )
  );
  if (snap.empty) return;

  const now = new Date().toISOString();
  let touched = 0;
  for (const d of snap.docs) {
    const data = d.data();
    if (data.archived === true) continue; // only open faults need the signal
    await updateDoc(doc(db, 'faults', d.id), {
      investigationHistory: arrayUnion({
        id: crypto.randomUUID(),
        timestamp: now,
        stage: data.stage || '01-Triage',
        author: byName,
        action: `Linked service ${statusLabel}`,
        details: reason
          ? `The customer service this fault is logged against was ${statusLabel}: ${reason}`
          : `The customer service this fault is logged against was ${statusLabel}.`,
      }),
      updatedAt: now,
    });
    touched++;
  }
  if (touched > 0) {
    console.log(
      `[cascadeServiceStatusToOpenFaults] Flagged ${touched} open fault(s) — service ${serviceId} ${statusLabel}`
    );
  }
}

/**
 * Plan-price cascade preview — pure read, used by the bulk update modal.
 *
 * For every customer_services doc on `planId` (Active / Suspended / Pending Install),
 * categorise it into one of three buckets:
 *   - matchedStandard: service.monthlyPrice ≈ oldPrice → safe to update
 *   - customPriced: monthlyPrice differs from oldPrice → grandfathered, will be SKIPPED
 *   - missingPrice: no monthlyPrice set → will be set to newPrice if cascaded
 *
 * Soft-deleted services and Cancelled / Inactive ones are excluded.
 */
export interface PlanCascadePreview {
  matchedStandard: CustomerService[]; // will be updated
  customPriced: CustomerService[];     // will be skipped
  missingPrice: CustomerService[];     // no monthlyPrice — will be set to newPrice
  totalCandidates: number;             // sum of the three buckets
}

/** Round to whole cents to dodge float jitter when comparing prices. */
function priceMatches(a: number | undefined | null, b: number): boolean {
  if (typeof a !== 'number' || !Number.isFinite(a)) return false;
  return Math.round(a * 100) === Math.round(b * 100);
}

export async function previewPlanPriceCascade(
  planId: string,
  oldPrice: number,
): Promise<PlanCascadePreview> {
  const snap = await getDocs(
    query(
      collection(db, 'customer_services'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('planId', '==', planId),
    ),
  );

  const matchedStandard: CustomerService[] = [];
  const customPriced: CustomerService[] = [];
  const missingPrice: CustomerService[] = [];

  for (const d of snap.docs) {
    const data = { id: d.id, ...d.data() } as CustomerService;

    // Skip soft-deleted and terminal states — we only cascade to live services.
    if ((data as { deleted?: boolean }).deleted) continue;
    if (data.status === 'Cancelled' || data.status === 'Inactive') continue;
    // One-time installs aren't recurring; they don't have a monthlyPrice to cascade.
    if (data.billingType === 'one-time') continue;

    if (data.monthlyPrice === undefined || data.monthlyPrice === null) {
      missingPrice.push(data);
    } else if (priceMatches(data.monthlyPrice, oldPrice)) {
      matchedStandard.push(data);
    } else {
      customPriced.push(data);
    }
  }

  return {
    matchedStandard,
    customPriced,
    missingPrice,
    totalCandidates: matchedStandard.length + customPriced.length + missingPrice.length,
  };
}

/**
 * Bulk-update a plan's sellPrice and cascade the new monthlyPrice to every
 * customer on that plan who is currently paying the OLD plan price.
 *
 * Custom-priced (grandfathered) services — where monthlyPrice differs from
 * oldPrice — are deliberately SKIPPED. Each updated service gets a
 * priceHistory audit entry referencing the bulk operation.
 *
 * Returns counts so the caller can show "X updated, Y skipped".
 *
 * @param planId          service_plans document ID
 * @param oldPrice        Previous sellPrice (used as the match key for "standard" customers)
 * @param newPrice        New sellPrice — written to plan + cascaded as monthlyPrice
 * @param reason          Human-readable reason — required for audit log
 * @param options.includeMissingPrice  If true, services with no monthlyPrice
 *                                     are also set to newPrice (default: true).
 *                                     They typically belong to customers who never had
 *                                     pricing wired up — folding them into the standard
 *                                     price is the safe default.
 */
export async function bulkUpdatePlanPrice(
  planId: string,
  oldPrice: number,
  newPrice: number,
  reason: string,
  options: { includeMissingPrice?: boolean } = {},
): Promise<{ updated: number; skipped: number; total: number }> {
  if (!Number.isFinite(newPrice) || newPrice < 0) {
    throw new Error('New price must be a non-negative number');
  }
  const trimmedReason = reason.trim();
  if (!trimmedReason) {
    throw new Error('A reason is required when bulk-updating plan price');
  }
  if (priceMatches(oldPrice, newPrice)) {
    throw new Error('New price is the same as the old price — nothing to update');
  }

  const includeMissingPrice = options.includeMissingPrice ?? true;
  const userId = localStorage.getItem('user_id') || 'unknown';
  const userName = localStorage.getItem('logged_in_user') || 'Unknown User';
  const now = new Date().toISOString();

  // 1. Update the plan itself + recompute margin via updateServicePlan equivalent.
  //    We avoid importing service-plans.service.ts to keep the dependency direction
  //    one-way (service-plans → customer-services, not the other way), so we write
  //    the plan doc directly here.
  const planRef = doc(db, 'service_plans', planId);
  const planSnap = await getDoc(planRef);
  if (!planSnap.exists()) throw new Error('Plan not found');
  const planData = planSnap.data() as { costPrice?: number };
  const costPrice = typeof planData.costPrice === 'number' ? planData.costPrice : 0;
  const margin =
    newPrice === 0
      ? 0
      : Math.round(((newPrice - costPrice) / newPrice) * 100 * 100) / 100;

  await updateDoc(planRef, {
    sellPrice: newPrice,
    margin,
    updatedAt: now,
  });

  // 2. Pull the cascade candidates and bucket them.
  const preview = await previewPlanPriceCascade(planId, oldPrice);

  const toUpdate: CustomerService[] = [
    ...preview.matchedStandard,
    ...(includeMissingPrice ? preview.missingPrice : []),
  ];

  if (toUpdate.length === 0) {
    return {
      updated: 0,
      skipped: preview.customPriced.length,
      total: preview.totalCandidates,
    };
  }

  // 3. Batch-update each matched service with monthlyPrice + priceHistory entry.
  let batch = writeBatch(db);
  let inBatch = 0;

  const auditReason = `Bulk plan update: ${trimmedReason}`;

  for (const svc of toUpdate) {
    if (!svc.id) continue;
    const change: ServicePriceChange = {
      previousPrice: svc.monthlyPrice ?? null,
      newPrice,
      reason: auditReason,
      changedAt: now,
      changedBy: userId,
      changedByName: userName,
    };
    batch.update(doc(db, 'customer_services', svc.id), {
      monthlyPrice: newPrice,
      priceHistory: arrayUnion(change),
      updatedAt: now,
      updatedBy: userId,
    });
    inBatch++;

    if (inBatch >= CASCADE_BATCH_LIMIT) {
      await batch.commit();
      batch = writeBatch(db);
      inBatch = 0;
    }
  }
  if (inBatch > 0) await batch.commit();

  console.log(
    `[bulkUpdatePlanPrice] Plan ${planId}: $${oldPrice} → $${newPrice}. Updated ${toUpdate.length}, skipped ${preview.customPriced.length} custom-priced.`,
  );

  return {
    updated: toUpdate.length,
    skipped: preview.customPriced.length,
    total: preview.totalCandidates,
  };
}

/**
 * Transfer a service to a different address.
 * Updates the address fields and logs the transfer.
 */
export async function transferServiceToAddress(
  serviceId: string,
  newAddress: string,
  newCity?: string,
  reason?: string
): Promise<void> {
  try {
    const userId = localStorage.getItem('user_id') || 'unknown';
    const now = new Date().toISOString();

    const updates: Record<string, unknown> = {
      address: newAddress,
      updatedAt: now,
      updatedBy: userId,
      lastTransfer: {
        type: 'address',
        transferredAt: now,
        transferredBy: userId,
        reason: reason || '',
      },
    };

    if (newCity !== undefined) {
      updates.city = newCity;
    }

    const serviceRef = doc(db, 'customer_services', serviceId);
    await updateDoc(serviceRef, updates);

    console.log('✅ Service transferred to address:', newAddress);
  } catch (error) {
    console.error('❌ Error transferring service to address:', error);
    throw error;
  }
}
